<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import BannerPicture from "/src/components/atoms/BannerPicture.vue";
import CampaignTitleBar from "/src/components/atoms/CampaignTitleBar.vue";
import SellInfo from "/src/components/atoms/SellInfo.vue";
import IndividualLiveSchedule from "/src/components/molecules/IndividualLiveSchedule.vue";
import GoodsList from "/src/components/molecules/GoodsList.vue";
// import GoodsSlider from "/src/components/molecules/GoodsSlider.vue";
import ItemRanking from "/src/components/molecules/ItemRanking.vue";
import RewardList from "/src/components/molecules/RewardList.vue";
import { formatDateTimeJaWithWeekday } from "@/utils/date.js";
import { useMeta } from "@/useMeta.js";

import db from "../main.js"; //追加
import {
  collection,
  getDocs,
  onSnapshot,
  doc,
  query,
  where,
  limit,
  orderBy,
  getDoc,
} from "firebase/firestore";
import { useRoute } from "vue-router";
import { useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();

// メタ情報の設定
const { updateMeta } = useMeta();

// 必要なrefの定義
const vtuber_image = ref(null);
const event_title = ref(null);
const eventdatestart = ref(null);
const eventdateend = ref(null);
const price = ref(null);
const shippingdate = ref(null);
const nowstreaming = ref(false);
const livedatefirst = ref();
const livedatesecond = ref();
const livedatethird = ref();
const livedatefourth = ref();
const livedateend = ref();
const totallivecount = ref();
const goodsData = ref([]);
const liveData = ref([]);
const rankingarray = ref([]);
const hasRankingProducts = ref(false);
const winningRateSur = ref("");
const winningRateSsr = ref("");
const winningRateR = ref("");
const winningRateA = ref("");
const winningRateB = ref("");
const winningRateC = ref("");
const rewardGoodsData = ref([]);

// 現在時刻
const now = ref(dayjs());
let timer;

dayjs.extend(customParseFormat);

// イベントが開始後1時間経ったかどうかの条件を計算
const isEventStartedAndElapsed = computed(() => {
  if (!eventdatestart.value) return false;
  const eventStartTime = dayjs(eventdatestart.value, "YYYY年MM月DD日HH:mm");
  return now.value.isAfter(eventStartTime.add(1, "hour"));
});

// イベントが終了したかどうかの条件を計算
const isEventend = computed(() => {
  if (!eventdateend.value) return false;
  const eventEndTime = dayjs(eventdateend.value, "YYYY年MM月DD日HH:mm");
  const currentTime = dayjs(now.value);
  return currentTime.isAfter(eventEndTime);
});

// コンポーネントのマウント時にデータを取得
const event_id = ref(null);
onMounted(async () => {
  event_id.value = route.params.event_id;
  if (!event_id.value) {
    console.error("event_id is undefined");
    return;
  }

  console.log("event_id:", event_id.value);
  await getEventData(event_id.value);
  await getProductsData(event_id.value);
  await getLiveData(event_id.value);
  await getpoint(event_id.value);
  await checkRankingProducts(event_id.value);
  await getWinningRateData(event_id.value);
  await getrewardGoodsData(event_id.value);

  // タイマーのセットアップ
  timer = setInterval(() => {
    now.value = dayjs();
  }, 1000);

  // データ読み込み完了後にプレレンダリングイベントを発火
  await nextTick();
  setTimeout(() => {
    window.dispatchEvent(new Event("render-event"));
  }, 1000); // 1秒待機してからイベント発火
});

onUnmounted(() => {
  clearInterval(timer);
});

const getEventData = async (event_id) => {
  onSnapshot(doc(db, "Goods_products", event_id), (doc) => {
    const data = doc.data();
    vtuber_image.value = data.vendor_image;
    event_title.value = data.eventtitle;
    eventdatestart.value = formatDateTimeJaWithWeekday(data.eventdate.start.toDate());
    eventdateend.value = formatDateTimeJaWithWeekday(data.eventdate.end.toDate());
    price.value = data.price;
    shippingdate.value = data.shipping_date;
    nowstreaming.value = data.nowstreaming;
    livedatefirst.value = data.livedate.first
      ? formatDateTimeJaWithWeekday(data.livedate.first.toDate())
      : null;
    livedatesecond.value = data.livedate.second
      ? formatDateTimeJaWithWeekday(data.livedate.second.toDate())
      : null;
    livedatethird.value = data.livedate.third
      ? formatDateTimeJaWithWeekday(data.livedate.third.toDate())
      : null;
    livedatefourth.value = data.livedate.fourth
      ? formatDateTimeJaWithWeekday(data.livedate.fourth.toDate())
      : null;
    livedateend.value = data.livedate.end
      ? formatDateTimeJaWithWeekday(data.livedate.end.toDate())
      : null;
    totallivecount.value = data.totallivecount;

    // メタデータの更新
    updateMeta({
      title: `${data.eventtitle} | SukiSukiらいぶすてーしょん`,
      description: `${data.eventtitle}で限定商品を当てよう！ライブ連動の、はずれなしくじ！`,
      ogTitle: `${data.eventtitle} | SukiSukiらいぶすてーしょん`,
      ogDescription: `${data.eventtitle}で限定商品を当てよう！ライブ連動の、はずれなしくじ！`,
      ogImage: data.vendor_image,
      ogType: "website",
      twitterCard: "summary_large_image",
      twitterTitle: `${data.eventtitle} | SukiSukiらいぶすてーしょん`,
      twitterDescription: `${data.eventtitle}で限定商品を当てよう！ライブ連動の、はずれなしくじ！`,
      twitterImage: data.vendor_image,
    });
  });
};

const getProductsData = async (event_id) => {
  const querySnapshot = await getDocs(collection(db, "Goods_products", event_id, "products"));
  goodsData.value = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
};

const getLiveData = async (event_id) => {
  const q = query(collection(db, "Goods_liveshedule"), where("event_id", "==", event_id));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    liveData.value.push(data);
  });
};

const getpoint = async (event_id) => {
  const querypoint = query(
    collection(db, "Goods_ranking", event_id, "ranking"),
    limit(3),
    orderBy("points", "desc")
  );
  onSnapshot(querypoint, (querySnapshot) => {
    rankingarray.value = [];
    if (querySnapshot.empty) {
      return;
    }
    querySnapshot.forEach((doc) => {
      const setranking = doc.data();
      rankingarray.value.push(setranking);
    });
  });
};

const checkRankingProducts = async (event_id) => {
  try {
    const querySnapshot = await getDocs(
      collection(db, "Goods_products", event_id, "rankingproducts")
    );
    if (!querySnapshot.empty) {
      hasRankingProducts.value = true;
    }
  } catch (error) {
    console.error("データの取得中にエラーが発生しました:", error);
  }
};

const getWinningRateData = async (event_id) => {
  try {
    const docRef = doc(db, "Goods_products", event_id, "rarity", "rarity");
    const docSnapshot = await getDoc(docRef);
    if (docSnapshot.exists()) {
      const data = docSnapshot.data();
      winningRateSur.value = (data.sur * 100).toFixed(2);
      winningRateSsr.value = (data.ssr * 100).toFixed(2);
      winningRateR.value = (data.r * 100).toFixed(2);
      winningRateA.value = (data.a * 100).toFixed(2);
      winningRateB.value = (data.b * 100).toFixed(2);
      winningRateC.value = (data.c * 100).toFixed(2);
    }
  } catch (error) {
    console.error("データの取得中にエラーが発生しました:", error);
  }
};

const getrewardGoodsData = async (event_id) => {
  const querySnapshot = await getDocs(collection(db, "Goods_products", event_id, "reward"));
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    rewardGoodsData.value.push(data);
  });
};

const toMain = () => {
  router.push(`/main?event_id=${event_id.value}`);
};

const toArchive = () => {
  router.push(`/archivelist?event_id=${event_id.value}`);
};

const toOOHP = () => {
  router.push(`/outofhourspurchase?event_id=${event_id.value}`);
};
</script>
<template>
  <v-row>
    <v-col cols="12">
      <BannerPicture :vtuber_image="vtuber_image" />
    </v-col>

    <v-col>
      <CampaignTitleBar :event_title="event_title" />
    </v-col>

    <v-col cols="12">
      <SellInfo
        :eventdatestart="eventdatestart"
        :eventdateend="eventdateend"
        :price="price"
        :shippingdate="shippingdate"
        :livedatefirst="livedatefirst"
        :livedatesecond="livedatesecond"
        :livedatethird="livedatethird"
        :livedatefourth="livedatefourth"
        :livedateend="livedateend"
        :totallivecount="totallivecount"
      />
    </v-col>

    <v-col cols="12" v-if="nowstreaming">
      <h3 class="title boxtitle" @click="toMain">配信を見る</h3>
    </v-col>
    <v-col cols="12" v-if="!nowstreaming && !isEventStartedAndElapsed">
      <h3 class="title boxtitlebefore">販売開始前</h3>
      <p class="attention">※配信が開始されましたら上のボタンより配信画面に入室できます</p>
    </v-col>

    <!-- イベントが開始されていて、かつ、配信中でない場合 -->
    <v-col cols="12" v-if="!nowstreaming && isEventStartedAndElapsed && !isEventend">
      <h3 class="title boxtitlebefore" @click="toArchive">アーカイブを見る＆時間外購入</h3>
      <p class="attention">※ライブ配信中は、上のボタンが「配信を見る」に変更され入室できます</p>
      <h3 class="title boxtitle" @click="toOOHP">時間外購入のみはこちら</h3>
    </v-col>

    <v-col cols="12" v-if="!nowstreaming && isEventend">
      <h3 class="title boxtitlebefore" @click="toArchive">販売終了（アーカイブを見る）</h3>
      <p class="attention">※このキャンペーンは販売終了したため、購入はできません。</p>
    </v-col>

    <v-col cols="12">
      <h3 class="title">配信スケジュール</h3>
      <IndividualLiveSchedule :liveData="liveData" />
    </v-col>

    <v-col cols="12">
      <h3 class="title">グッズ一覧</h3>
      <GoodsList
        :goodsData="goodsData"
        :winningRateSur="winningRateSur"
        :winningRateSsr="winningRateSsr"
        :winningRateR="winningRateR"
        :winningRateA="winningRateA"
        :winningRateB="winningRateB"
        :winningRateC="winningRateC"
      />
    </v-col>

    <v-col cols="12" v-if="hasRankingProducts">
      <h3 class="title">ランキング</h3>
      <ItemRanking :rankingarray="rankingarray" :rankingGoodsData="rankingGoodsData" />
    </v-col>

    <v-col cols="12" v-if="rewardGoodsData.length > 0">
      <h3 class="title">特典</h3>
      <RewardList :rewardGoodsData="rewardGoodsData" />
    </v-col>
  </v-row>
</template>

<style scoped>
.title {
  margin: 40px 0 30px 0;
}
.boxtitle {
  border: solid 1px rgb(218, 57, 144);
  display: inline-block;
  padding: 15px 15px 15px 15px;
  background-color: rgb(218, 57, 144);
  border-radius: 20px 20px 20px 20px;
  color: white;
  cursor: pointer;

  /* text-align: left; */
}
.boxtitlebefore {
  border: solid 1px rgb(204, 204, 204);
  display: inline-block;
  padding: 15px 15px 15px 15px;
  background-color: rgb(204, 204, 204);
  border-radius: 20px 20px 20px 20px;
  color: white;
  cursor: pointer;

  /* text-align: left; */
}
.attention {
  margin: 0px 5px 0px 5px;
}
</style>
